import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { menuBar, MenuBarButton, MenuBarDateNavigator } from '@softapps/core';
import {
  BackNavigable,
  BackNavigationService,
  menuItems,
  showRequestErrors,
} from '@softline/application';
import { DateService, IsoDate, parseIso8601, RequestError, Store, toIsoDate, Trace } from '@softline/core';
import {
  MessageBarStore,
  modalFn,
  ModalStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL,
  translator,
} from '@softline/ui-core';
import { combineLatest, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Timestamp } from '../../data/timestamp.model';
import {
  SOFTLINE_FEATURE_TIMESTAMP,
  SOFTLINE_FEATURE_TIMESTAMP_CURRENT_EMPLOYEE,
  TimestampStore,
} from '../../stempelerfassung.shared';
import { TimestampCurrentEmployeeStore } from '../../store/current-employee.store';
import { RecordTimestampDialogComponent } from '../dialogs/record-timestamp-dialog/record-timestamp-dialog.component';

import { IconComponent, RepeatDirective, TranslatePipe } from '@softline/ui-core';
import { DateNavigatorComponent } from '@softline/ui-core/date';
@Component({
  // tslint:disable-next-line:component-selector
  selector: 'soft-timestamp-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.css'],
  imports: [
    CommonModule,
    MenuBarDateNavigator,
    MenuBarButton,
    DateNavigatorComponent,
    IconComponent,
    RepeatDirective,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DatePipe],
})
export class OverviewComponent implements OnInit, OnDestroy, BackNavigable {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dateService = inject(DateService);
  private readonly backNavigationService = inject(BackNavigationService);

  private subscription?: Subscription;
  private allTimestamps = this.store.signal(
    SOFTLINE_FEATURE_TIMESTAMP,
    TimestampStore.getters.all
  );
  private modal = modalFn();

  currentEmployee$ = this.store.observe(
    SOFTLINE_FEATURE_TIMESTAMP_CURRENT_EMPLOYEE,
    TimestampCurrentEmployeeStore.getters.data
  );

  date$ = this.activatedRoute.fragment.pipe(
    map((o) => (o ?? this.dateService.now()) as IsoDate)
  );

  loaded = this.store.signal(SOFTLINE_FEATURE_TIMESTAMP, TimestampStore.getters.loaded);

  loading = this.store.signal(SOFTLINE_FEATURE_TIMESTAMP, TimestampStore.getters.loading);
  //date = toSignal(this.date$, { initialValue: '1970-01-01' as IsoDate });
  date = signal<IsoDate>(this.dateService.now());

  timestamps = computed(() => {
    const timestamps = this.allTimestamps();
    const date = this.date();
    return timestamps
      .filter((o) => {
        const time = parseIso8601(o.timestamp);
        return time !== null && toIsoDate(new Date(time)) === date;
      })
      .sort((a, b) =>
        a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0
      );
  });

  private t = translator();

  readonly actionBarTemplate = viewChild.required('actionBar', {
    read: TemplateRef,
  });
  readonly trailingActionBarTemplate = viewChild.required('trailingActionBar', {
    read: TemplateRef,
  });

  constructor() {
    menuBar({
      key: 'softapps/pms/stempelerfassung/overview',
      title: this.t('Stempel'),
      separated: true,
      template: this.actionBarTemplate,
    });
    menuBar({
      key: 'softapps/pms/stempelerfassung/overview',
      title: this.t('Stempel'),
      separated: true,
      align: 'trailing',
      template: this.trailingActionBarTemplate,
    });
    menuItems(() => {
      return [
        {
          title: 'Erfassen',
          name: 'create-timestamp',
          outlet: 'bottom',
          type: 'execute',
          class: 'soft-button primary',
          canExecute: true,
          execute: async () => await this.onCreateClick(),
        },
      ];
    });
  }

  ngOnInit(): void {
    this.backNavigationService.set(this);

    this.date$.subscribe((d) => this.date.set(d));

    this.subscription = combineLatest([this.currentEmployee$, this.date$]).subscribe(
      async ([employee, date]) => {
        if (!employee) {
          return;
        }
        if (!date) {
          date = this.dateService.now();
        }
        this.store.commit(SOFTLINE_FEATURE_TIMESTAMP, TimestampStore.mutations.clear);
        await this.store.dispatch(
          SOFTLINE_FEATURE_TIMESTAMP,
          TimestampStore.actions.loadMany,
          {
            queryParams: {
              from: date.substr(0, 10),
              to: date.substr(0, 10),
              employeeid: employee.id,
            },
          }
        );
      }
    );

    // this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
    //   name: OverviewComponent,
    //   commands: this.createCommands(),
    // });
  }

  async onDateChange(date: IsoDate): Promise<void> {
    this.date.set(date);
    await this.router.navigate(['stempelerfassung'], { fragment: date });
  }

  ngOnDestroy(): void {
    this.backNavigationService.set(undefined);
    // this.store.commit(
    //   SOFTLINE_FEATURE_COMMANDS,
    //   CommandStore.mutations.removeSet,
    //   OverviewComponent
    // );
    if (this.subscription && !this.subscription.closed) this.subscription.unsubscribe();
    this.subscription = undefined;
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/']);
  }

  // protected createCommands(): Command[] {
  //   return [
  //     {
  //       icon: 'fa-regular fa-stopwatch',
  //       name: '#TIMESTAMP.RECORD_TIMESTAMP',
  //       class: 'menu action-menu action-menu-top',
  //       execute: async () => await this.onCreateClick(),
  //     },
  //     {
  //       icon: 'fa-regular fa-stopwatch',
  //       name: '#TIMESTAMP.RECORD_TIMESTAMP',
  //       class: 'menu bottom-menu accent sm:hidden',
  //       execute: async () => await this.onCreateClick(),
  //     },
  //   ];
  // }

  async onDelete(entity: Timestamp): Promise<void> {
    await this.store
      .dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.ask, {
        question: '#TIMESTAMP.ASK.CONFIRM_DELETE',
        dismiss: { escape: true, backdrop: true },
      })
      .then(async (result) => {
        if (result !== 'YES') {
          return;
        }
        try {
          await this.store.dispatch(
            SOFTLINE_FEATURE_TIMESTAMP,
            TimestampStore.actions.delete,
            { entity }
          );
          await this.store.dispatch(
            SOFTLINE_FEATURE_MESSAGE_BAR,
            MessageBarStore.actions.success,
            {
              title: '#TIMESTAMP.MESSAGES.REMOVE_SUCCESS.TITLE',
              message: '#TIMESTAMP.MESSAGES.REMOVE_SUCCESS.MESSAGE',
            }
          );
        } catch (e) {
          if (e instanceof RequestError) {
            await this.store.dispatch(
              SOFTLINE_FEATURE_MESSAGE_BAR,
              MessageBarStore.actions.error,
              {
                title: '#TIMESTAMP.MESSAGES.REMOVE_FAILURE.TITLE',
                message: '#TIMESTAMP.MESSAGES.REMOVE_FAILURE.MESSAGE',
              }
            );
          } else {
            showRequestErrors(this.store, e);
          }
        }
      });
  }

  async onNavigateDate(date: string) {
    await this.router.navigate(['stempelerfassung'], { fragment: date });
  }

  @Trace()
  async onCreateClick(): Promise<void> {
    const result = await this.modal({
      component: RecordTimestampDialogComponent,
      title: 'Stempel erfassen',
      subtitle: 'Stempelart und Uhrzeit auswählen',
      icon: 'fa-regular fa-clock',
    });
    if (result !== 'DISMISSED') await this.addTimestamp(this.store, result);
  }

  async addTimestamp(store: Store, entity: Timestamp): Promise<void> {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_TIMESTAMP,
        TimestampStore.actions.create,
        { entity }
      );
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        {
          title: '#TIMESTAMP.MESSAGES.ADD_SUCCESS.TITLE',
          message: '#TIMESTAMP.MESSAGES.ADD_SUCCESS.MESSAGE',
        }
      );
      await this.router.navigate(['/stempelerfassung']);
    } catch (e) {
      if (e instanceof RequestError) {
        await this.store.dispatch(
          SOFTLINE_FEATURE_MESSAGE_BAR,
          MessageBarStore.actions.error,
          {
            title: '#TIMESTAMP.MESSAGES.ADD_FAILURE.TITLE',
            message: e.message,
          }
        );
      } else {
        showRequestErrors(this.store, e);
      }
    }
  }
}
